import { useEffect, useState } from "react";
import { FEATURES } from "@/constants";
import { ArrowRightIcon } from "lucide-react";
import Container from "../global/container";
import { MagicCard } from "../ui/magic-card";
import { Button } from "../ui/button";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import PerformanceMetrics from "./performance-metrics";
import SecurityShield from "./security-shield";
import AISolutions from "./ai-solutions";
import DevelopmentMetrics from "./DevelopmentMetrics";
import RealTimeUpdates from "./RealTimeUpdates";

const MOBILE_BREAKPOINT = 768;

const Features = () => {
  const [isMobile, setIsMobile] = useState(false);
  const isGlobalCdnEnabled = useFeatureFlag("globalCdn");
  const isPerformanceEnabled = useFeatureFlag("performance");
  const isSecurityEnabled = useFeatureFlag("security");
  const isAiSolutionsEnabled = useFeatureFlag("aiSolutions");
  const isDevelopmentEnabled = useFeatureFlag("development");
  const isRealtimeEnabled = useFeatureFlag("realtime");

  // Filter and order the features as needed
  const orderedFeatures = [
    FEATURES.find((f) => f.title === "Performance"),
    FEATURES.find((f) => f.title === "Security"),
    FEATURES.find((f) => f.title === "AI Solutions"),
    FEATURES.find((f) => f.title === "Development"),
    FEATURES.find((f) => f.title === "Real-time"),
  ]
    .filter(Boolean)
    .filter((feature) => {
      switch (feature?.title) {
        case "Global CDN":
          return isGlobalCdnEnabled;
        case "Performance":
          return isPerformanceEnabled;
        case "Security":
          return isSecurityEnabled;
        case "AI Solutions":
          return isAiSolutionsEnabled;
        case "Development":
          return isDevelopmentEnabled;
        case "Real-time":
          return isRealtimeEnabled;
        default:
          return true;
      }
    }) as typeof FEATURES;

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getFeatureStyles = (feature: (typeof FEATURES)[0]) => {
    if (feature.title === "AI Solutions") {
      return {
        className: "md:col-span-2 md:h-[360px] lg:col-span-2",
        contentClassName: "h-[360px]",
      };
    }
    if (feature.title === "Development") {
      return {
        className: "lg:col-start-3 lg:row-start-1",
        contentClassName: "h-[300px]",
      };
    }
    if (feature.title === "Real-time") {
      return {
        className: "",
        contentClassName: "h-[300px]",
      };
    }
    return {
      className: "",
      contentClassName: "h-[300px]",
    };
  };

  const displayedFeatures: typeof FEATURES = isMobile
    ? (orderedFeatures.slice(0, 4) as typeof FEATURES)
    : orderedFeatures;

  const getFeatureComponent = (feature: (typeof FEATURES)[0]) => {
    switch (feature.title) {
      case "Performance":
        return <PerformanceMetrics data-oid="8:-.13s" />;
      case "Security":
        return <SecurityShield data-oid="72_lqh-" />;
      case "AI Solutions":
        return (
          <div
            className="relative z-30 w-full h-full bg-transparent"
            data-oid="ttp_n.i"
          >
            <div
              className="w-full h-auto sm:h-[230px] overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent pr-2 pb-0"
              data-oid="u.rm1o0"
            >
              <AISolutions data-oid="f4.:r3h" />
            </div>
          </div>
        );

      case "Development":
        return <DevelopmentMetrics data-oid="ms_a525" />;
      case "Real-time":
        return <RealTimeUpdates data-oid="w7_-vqj" />;
      default:
        return null;
    }
  };

  return (
    <div
      className="relative flex flex-col items-center justify-center w-full py-20"
      data-oid="8yxcrml"
    >
      <Container data-oid="h:9i:n9">
        <div
          className="flex flex-col items-center text-center max-w-2xl mx-auto"
          data-oid="2m-fn.k"
        >
          <h2
            className="text-2xl md:text-4xl lg:text-5xl font-heading font-medium !leading-snug mt-6"
            data-oid=".-4qp4q"
          >
            AI-Powered marketing <br data-oid="q.dq87d" /> made{" "}
            <span className="font-subheading italic" data-oid="3kregcg">
              simple
            </span>
          </h2>
          <p
            className="text-base md:text-lg text-center text-accent-foreground/80 mt-6"
            data-oid="18pjy77"
          >
            Transform your marketing with AI-powered automation. Create
            campaigns faster, generate better content, and make smarter
            decisions in minutes.
          </p>
        </div>
      </Container>

      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 relative overflow-visible"
        data-oid="mmvs4ns"
      >
        {displayedFeatures.map((feature: (typeof FEATURES)[0], index: number) => {
          const styles = getFeatureStyles(feature);
          const containerClassName = isMobile
            ? "h-full"
            : styles.className;
          const contentClassName = isMobile
            ? "min-h-[240px]"
            : styles.contentClassName;
          return (
            <Container
              key={feature.title}
              delay={0.1 + index * 0.1}
              className={containerClassName}
              data-oid="hvf2cev"
            >
              <MagicCard
                gradientFrom="#38bdf8"
                gradientTo="#3b82f6"
                className="flex h-full flex-col p-4 lg:p-6 lg:rounded-3xl group hover:scale-105 transition-transform duration-300"
                gradientColor="rgba(59,130,246,0.1)"
                data-oid="ak7splb"
              >
                <div className="flex items-center space-x-4 mb-4" data-oid="8e7shij">
                  <h3
                    className="text-xl font-semibold flex items-center gap-2"
                    data-oid="ova4ujf"
                  >
                    <feature.icon
                      className="size-5 text-primary"
                      data-oid="pne0dva"
                    />
                    {feature.title}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground" data-oid="1u1d8id">
                  {feature.description}
                </p>

                <div
                  className={`mt-6 w-full flex-1 bg-card/50 overflow-hidden rounded-xl ${contentClassName}`}
                  data-oid="-130fuc"
                >
                  {getFeatureComponent(feature)}
                </div>
              </MagicCard>
            </Container>
          );
        })}
      </div>
    </div>
  );
};

export default Features;
